<script setup>
import {computed, ref, watch} from "vue";
import {useForm, usePage} from "@inertiajs/inertia-vue3";
import {useHelpers, useTenantTextRules} from "@/helpers";
import MediaUpload from "../../Common/Media/MediaUpload.vue";
import SecondaryButton from "../../SecondaryButton.vue";
import PrimaryButton from "../../PrimaryButton.vue";
import DialogModal from "@/Components/DialogModal.vue";
import InputLabel from "@/Components/InputLabel.vue";
import TextInput from "@/Components/TextInput.vue";
import InputError from "@/Components/InputError.vue";

const props = defineProps({
  hideSave: {
    type: Boolean,
    default() {
      return false;
    },
  },
  publicProjectUploadUrl: {
    type: String,
    default: null,
  },
});

const {resolveTextRule} = useTenantTextRules();

/**
 * Clipboard API needs a secure context (HTTPS / localhost). Use execCommand fallback on HTTP or if writeText fails.
 */
const copyPublicLink = async () => {
  const text = props.publicProjectUploadUrl;
  if (!text) {
    return;
  }

  if (typeof window !== 'undefined' && window.isSecureContext && navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return;
    } catch {
      /* fallback below */
    }
  }

  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.left = '-9999px';
  document.body.appendChild(textarea);
  textarea.focus();
  textarea.select();
  textarea.setSelectionRange(0, text.length);
  try {
    document.execCommand('copy');
  } finally {
    document.body.removeChild(textarea);
  }
};

const currentUser = usePage().props.value.auth.user;
const helpers = useHelpers();
const projectProperty = computed(() => usePage().props.value.project);
let project = projectProperty.value;
watch(projectProperty, () => {
  project = projectProperty.value;
});

const showSmsModal = ref(false);
const smsForm = useForm({
  phone: '',
});

const canSendPublicUploadSms = computed(
  () => helpers.userCanManageProject(currentUser) || helpers.userIsVendor(currentUser),
);

const openSmsModal = () => {
  smsForm.clearErrors();
  smsForm.reset();
  showSmsModal.value = true;
};

const closeSmsModal = () => {
  showSmsModal.value = false;
  smsForm.reset();
};

const sendPublicUploadSms = () => {
  smsForm.post(route('admin.projects.public-upload-sms', {project: project.pid}), {
    preserveScroll: true,
    onSuccess: () => {
      closeSmsModal();
    },
  });
};
</script>

<template>
  <div v-if="publicProjectUploadUrl" class="mb-6 p-4 bg-gray-50 border border-gray-200 rounded-lg space-y-2">
    <p class="text-sm font-medium text-gray-800">
      {{
        resolveTextRule(
          "project.uploads.public_link_heading",
          "Link for subcontractors",
          "project.uploads"
        )
      }}
    </p>
    <p class="text-xs text-gray-600 break-all font-mono">
      {{ publicProjectUploadUrl }}
    </p>
    <div class="flex flex-wrap gap-2">
      <SecondaryButton type="button" @click="copyPublicLink">
        {{
          resolveTextRule(
            "project.uploads.public_link_copy",
            "Copy link",
            "project.uploads"
          )
        }}
      </SecondaryButton>
      <!--<SecondaryButton
        v-if="canSendPublicUploadSms"
        type="button"
        @click="openSmsModal"
      >
        {{
          resolveTextRule(
            "project.uploads.public_link_send_sms",
            "Send via SMS",
            "project.uploads"
          )
        }}
      </SecondaryButton>-->
    </div>
    <DialogModal :show="showSmsModal" max-width="lg" @close="closeSmsModal">
      <template #title>
        {{
          resolveTextRule(
            "project.uploads.public_link_sms_modal_title",
            "Send upload link by SMS",
            "project.uploads"
          )
        }}
      </template>
      <template #content>
        <p class="text-sm text-gray-600 mb-4">
          {{
            resolveTextRule(
              "project.uploads.public_link_sms_modal_hint",
              "Enter the recipient number in international format (E.164), for example +15551234567.",
              "project.uploads"
            )
          }}
        </p>
        <div>
          <InputLabel
            for="public-upload-sms-phone"
            :value="
              resolveTextRule(
                'project.uploads.public_link_sms_phone_label',
                'Phone number',
                'project.uploads'
              )
            "
          />
          <TextInput
            id="public-upload-sms-phone"
            v-model="smsForm.phone"
            type="tel"
            class="mt-1 block w-full"
            autocomplete="tel"
            placeholder="+15551234567"
            @keyup.enter="sendPublicUploadSms"
          />
          <InputError class="mt-2" :message="smsForm.errors.phone" />
        </div>
      </template>
      <template #footer>
        <SecondaryButton class="mr-2" type="button" @click="closeSmsModal">
          {{
            resolveTextRule(
              "project.uploads.public_link_sms_cancel",
              "Cancel",
              "project.uploads"
            )
          }}
        </SecondaryButton>
        <PrimaryButton
          type="button"
          :class="{ 'opacity-25': smsForm.processing }"
          :disabled="smsForm.processing"
          @click="sendPublicUploadSms"
        >
          {{
            resolveTextRule(
              "project.uploads.public_link_sms_send",
              "Send SMS",
              "project.uploads"
            )
          }}
        </PrimaryButton>
      </template>
    </DialogModal>
  </div>
  <MediaUpload
    :media-input="{'I': project.image_media, 'F': project.file_media}"
    :can-see-upload-form="(helpers.userCanManageProject(currentUser) || helpers.userIsVendor(currentUser))"
    :can-modify-media-details="helpers.userCanManageProject(currentUser)"
    :hide-save="hideSave"
    media-location="upload"
    :extra-attributes="{pid: project.pid}"
    :can-see-files="helpers.userCanManageProject(currentUser)"
    :show-check-button="true"
  ></MediaUpload>
</template>
